import { FC, memo, useCallback, useEffect, useMemo } from "react";
import { ScreenProps } from "../typings";
import { FormView as IFormView } from "@data-root/types";
import FloatButton from "../components/FloatButton";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Inline,
  useAppContext,
  useAsyncCallback,
  useClientContext,
  useNavigation,
  useRow,
  UseRowContext,
  View,
  ViewHeader,
} from "@data-root/react";
import { App as AntdApp, Empty, Layout, Tooltip } from "antd";
import { Form } from "../components/inline/Form";
import { useQueryClient } from "@tanstack/react-query";
import { useServices } from "@/hooks/useService";
import { ErrorView } from "@data-root/error-boundary";

const { Content } = Layout;

export const FormView: FC<ScreenProps<IFormView>> = memo(({ view }) => {
  const { navigation } = useNavigation();

  const queryClient = useQueryClient();

  const rowId = navigation.getParam("rowId");

  const deltaId = navigation.getParam("deltaId");

  const antdApp = AntdApp.useApp();

  const { tableId } = view;

  const { core } = useClientContext();
  const { appSpecifier, getTable, styles } = useAppContext();

  const hooks = useMemo(
    () => ({
      viewId: view.id,
      "before:invalidate": async () => {
        const parentTableId = navigation.getParam("parentTableId");
        const parentRowId = navigation.getParam("parentRowId");
        if (parentTableId && parentRowId) {
          core.cache.invalidateQuery([
            "row",
            appSpecifier,
            parentTableId,
            parentRowId,
          ]);
          await queryClient.invalidateQueries({
            queryKey: ["row", appSpecifier, parentTableId, parentRowId],
          });
        }
      },
    }),
    [appSpecifier, core.cache, navigation, queryClient, view.id]
  );

  const table = useMemo(() => getTable(tableId), [getTable, tableId]);

  const useRowHook = useRow(core, appSpecifier, tableId, rowId, hooks);
  const { getContext, loaded, commit, canCommit, error } = useRowHook;

  const cancel = useAsyncCallback(async () => {
    if (deltaId) await core.undo(appSpecifier, deltaId);
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [deltaId, core, appSpecifier, navigation]);

  const saveAnyWay = useAsyncCallback(async () => {
    try {
      await commit();
    } catch (error) {
      antdApp.modal.error({
        title: "Error al guardar",
        content: "Ocurrió un error al guardar los datos",
        centered: true,
        okText: "Aceptar",
      });
      return;
    }

    core.emitter.emit("pending_sync", core);

    if (view.onSave) {
      navigation.navigate(view.onSave);
    } else {
      navigation.goBack();
    }

    if (view.onSaveAction) {
      const context = getContext();
      await core.run(appSpecifier, view.onSaveAction, context);
    }
  }, [antdApp.modal, commit, core, navigation, view]);

  const save = useAsyncCallback(async () => {
    const result = await canCommit();
    if (!result.canCommit) {
      return antdApp.modal.warning({
        title: "Existen campos vacíos",
        content: result.reason,
        centered: true,
        okCancel: true,
        onCancel: saveAnyWay,
        cancelText: "Guardar de todos modos",
        okText: "Cancelar",
      });
    }

    antdApp.modal.confirm({
      title: "¿Deseas seguir editando?",
      content: "Puedes guardar y seguir editando o guardar y salir.",
      centered: true,
      okText: "Guardar y salir",
      cancelText: "Guardar y seguir editando",
      onOk: async () => {
        try {
          await commit();
          core.emitter.emit("pending_sync", core);

          if (view.onSave) {
            navigation.navigate(view.onSave);
          } else {
            navigation.goBack();
          }

          if (view.onSaveAction) {
            const context = getContext();
            await core.run(appSpecifier, view.onSaveAction, context);
          }
        } catch (error) {
          antdApp.modal.error({
            title: "Error al guardar",
            content: "Ocurrió un error al guardar los datos",
            centered: true,
            okText: "Aceptar",
          });
        }
      },
      onCancel: async () => {
        try {
          await commit();
          core.emitter.emit("pending_sync", core);

          antdApp.notification.success({
            message: "Guardado exitoso",
            description: "Los cambios se han guardado correctamente.",
          });
        } catch (error) {
          antdApp.modal.error({
            title: "Error al guardar",
            content: "Ocurrió un error al guardar los datos",
            centered: true,
            okText: "Aceptar",
          });
        }
      },
    });
  }, [
    canCommit,
    antdApp.modal,
    antdApp.notification,
    saveAnyWay,
    commit,
    core,
    view.onSave,
    navigation,
  ]);

  const onBack = useAsyncCallback(
    async (_, prevent: () => void) => {
      prevent();
      const result = await canCommit();

      if (result.canCommit) return await save();

      antdApp.modal.warning({
        title: "No se puede Guardar",
        content: result.reason,
        centered: true,
        okCancel: true,
        onCancel: cancel,
        cancelText: "Salir sin Guardar",
      });
    },
    [canCommit, antdApp.modal, cancel, save]
  );

  const onDrop = useCallback(
    ({ id, data }: any) => {
      if (id !== view.id) return;

      navigation.setParams({ rowId: data.rowId });
    },
    [navigation, view.id]
  );

  const onNew = useAsyncCallback(async () => {
    const row = await core.emptyRow(
      appSpecifier,
      tableId,
      undefined,
      undefined,
      undefined,
      undefined
    );

    const { rowId } = await core.add(appSpecifier, tableId, row);

    navigation.setParams({ rowId });
  }, [core, appSpecifier, tableId, navigation]);

  useEffect(() => {
    core.emitter.on("back", onBack);

    core.emitter.on("drop", onDrop);

    return () => {
      core.emitter.off("back", onBack);
      core.emitter.off("drop", onDrop);
    };
  }, [core, navigation, onBack, onDrop, view.id]);

  const stableContext = getContext();

  useServices(view.serviceIds, stableContext);

  if (error) return <ErrorView error={error} />;

  if (!rowId)
    return (
      <View style={{ height: "100%" }}>
        <Empty style={{ margin: "auto" }} />
        <FloatButton
          key="new"
          shape="square"
          onClick={onNew}
          icon={
            <PlusOutlined
              style={{
                color: styles.darkTintColor,
              }}
            />
          }
        />
      </View>
    );

  return (
    <UseRowContext.Provider value={useRowHook}>
      <Inline table={table} rowId={rowId} view={view}>
        <Layout>
          <ViewHeader loaded={loaded} view={view} getContext={getContext} />

          <Content style={style}>
            <Form getContext={getContext} loaded={loaded} />
          </Content>

          <FloatButton
            shape="square"
            type="primary"
            onClick={save}
            icon={
              <Tooltip key="save" title="Guardar">
                <SaveOutlined />
              </Tooltip>
            }
          />
        </Layout>
      </Inline>
    </UseRowContext.Provider>
  );
});

const style = {
  overflow: "auto",
  padding: 10,
  paddingTop: 0,
  paddingBottom: 100,
};
